import {
  ApiApplicationServicesDataTypesPqIndexTypesPqIndex,
  ApiControllersDataTypesMeasurementDeviceGrouping,
  ApiControllersDataTypesMeasurementParameterGrouping,
  ApiControllersDataTypesTimeGrouping,
} from '@redux/apiSlices/pqIndexApi';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../store';

export interface PqIndexState {
  appliedParameters: AppliedParameters;
  indices: {
    deviceParameterIndices: ApiApplicationServicesDataTypesPqIndexTypesPqIndex[][] | undefined;
    parameterTimeIndices: ApiApplicationServicesDataTypesPqIndexTypesPqIndex[][] | undefined;
    deviceTimeIndices: ApiApplicationServicesDataTypesPqIndexTypesPqIndex[][] | undefined;
  };
  dataGridSelectedDimensions: { [key: string]: string | undefined };
  deviceDetails: { [key: string]: DeviceDetailsType };
  parameterDetails: { [key: number]: SelectedParameterDetails };
  payload: Payload;
}

type TsosStateProps = {
  id: number;
  name: string;
  active: boolean;
  abbreviation: string;
};

export type Payload = {
  from: string;
  to: string;
  timeGrouping?: ApiControllersDataTypesTimeGrouping;
  measurementDeviceGrouping?: ApiControllersDataTypesMeasurementDeviceGrouping;
  measurementParameterGrouping?: ApiControllersDataTypesMeasurementParameterGrouping;
  limitSetId: number;
  apiControllersPqIndexParameters: {
    measurementDeviceIds: number[];
    measurementParameterIds: number[];
  };
};

export type DeviceDetailsType = {
  tsoId: number;
  deviceName: string;
  measurementPointName: string;
  substationName: string;
};

export type SelectedParameterDetails = {
  id: string;
  name: string;
  category: string;
};

type Option = {
  id: number;
  value?: string;
  label: string;
};

export type AppliedParameters = {
  tsos: TsosStateProps[];
  start: string | undefined;
  end: string | undefined;
  limitSet: Option | undefined | null;
  parameters: string[] | undefined;
  devices: { [key: string]: string[] } | undefined;
};

const initialState: PqIndexState = {
  appliedParameters: {
    tsos: [],
    start: undefined,
    end: undefined,
    limitSet: undefined,
    parameters: undefined,
    devices: undefined,
  },
  indices: {
    deviceParameterIndices: undefined,
    parameterTimeIndices: undefined,
    deviceTimeIndices: undefined,
  },

  dataGridSelectedDimensions: {
    measurement: 'Messgerät',
    timespan: 'Kalenderwochen',
    parameterCategory: undefined,
  },
  payload: {
    from: '',
    to: '',
    limitSetId: NaN,
    apiControllersPqIndexParameters: {
      measurementDeviceIds: [],
      measurementParameterIds: [],
    },
  },
  deviceDetails: {},
  parameterDetails: {},
};

const pqIndexSlice = createSlice({
  name: 'PqIndex',
  initialState,
  reducers: {
    setDeviceParameterIndices: (
      state,
      action: PayloadAction<ApiApplicationServicesDataTypesPqIndexTypesPqIndex[][] | undefined>
    ) => {
      state.indices.deviceParameterIndices = action.payload;
    },
    setParameterTimeIndices: (state, action: PayloadAction<ApiApplicationServicesDataTypesPqIndexTypesPqIndex[][] | undefined>) => {
      state.indices.parameterTimeIndices = action.payload;
    },
    setDeviceTimeIndices: (state, action: PayloadAction<ApiApplicationServicesDataTypesPqIndexTypesPqIndex[][] | undefined>) => {
      state.indices.deviceTimeIndices = action.payload;
    },
    setDataGridSelectedDimensions: (state, action: PayloadAction<{ [key: string]: any }>) => {
      state.dataGridSelectedDimensions = action.payload;
    },
    setDeviceDetails: (state, action: PayloadAction<{ [key: string]: DeviceDetailsType }>) => {
      state.deviceDetails = action.payload;
    },
    setParametersDetails: (state, action: PayloadAction<{ [key: number]: SelectedParameterDetails }>) => {
      state.parameterDetails = action.payload;
    },
    setParametersPayload: (state, action: PayloadAction<Payload>) => {
      state.payload = action.payload;
    },
    setAppliedParameters: (state, action: PayloadAction<AppliedParameters>) => {
      state.appliedParameters = action.payload;
    },
  },
});

export const selectDeviceIndices = (state: RootState) => state.pqIndex.indices;

export const selectParameterPayload = (state: RootState) => state.pqIndex.payload;

export const selectDeviceParameterIndices = (state: RootState) => state.pqIndex.indices.deviceParameterIndices;

export const selectParameterTimeIndices = (state: RootState) => state.pqIndex.indices.parameterTimeIndices;

export const selectDeviceTimeIndices = (state: RootState) => state.pqIndex.indices.deviceTimeIndices;

export const selectDataGridSelectedDimensions = (state: RootState) => state.pqIndex.dataGridSelectedDimensions;

export const selectDeviceDetails = (state: RootState) => state.pqIndex.deviceDetails;

export const selectParameterDetails = (state: RootState) => state.pqIndex.parameterDetails;

export const selectAppliedParameters = (state: RootState) => state.pqIndex.appliedParameters;

export const {
  setDeviceParameterIndices,
  setParameterTimeIndices,
  setDeviceTimeIndices,
  setDataGridSelectedDimensions,
  setDeviceDetails,
  setParametersDetails,
  setParametersPayload,
  setAppliedParameters,
} = pqIndexSlice.actions;

export default pqIndexSlice.reducer;
